import { useState, useEffect } from 'react';
import { isCrawlFeed, crawlUrl, normalizeCrawlItem } from '../utils/crawlFeedAdapter';

const apiKey = process.env.REACT_APP_API_KEY

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Allow items dated slightly in the "future" to account for timezone offsets
// and clock skew between the API and the client. Anything beyond this window
// is treated as a bad/future-dated record and filtered out of the feed.
const FUTURE_DATE_TOLERANCE_MS = 12 * 60 * 60 * 1000; // 12 hours

// Extra items to request beyond `limit`. Future-dated records sort to the top
// of a newest-first feed, so when we drop them we need spare items behind them
// to still fill the requested count (otherwise a limit=1 card renders empty).
const FUTURE_DATE_FETCH_BUFFER = 5;

/**
 * Clears expired feed cache entries from localStorage
 */
const clearExpiredCache = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('feed_') && key.endsWith('_timestamp')) {
        const timestamp = parseInt(localStorage.getItem(key), 10);
        if (isNaN(timestamp) || now - timestamp > CACHE_DURATION) {
          const dataKey = key.replace('_timestamp', '');
          keysToRemove.push(key, dataKey);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore cleanup errors
  }
};

/**
 * Normalize an API response into a flat array of posts.
 *
 * Most endpoints (/posts/recent, /departments/) return a flat array. Newer ones
 * like /posts/recent/schema may instead return an envelope ({ results: [...] })
 * or a map grouped by collection ({ house_of_commons: [...], senate_news: [...] }).
 * This collapses all of those to a single array so the UI can render them.
 */
const extractPostsArray = (result) => {
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object') {
    // Envelope shapes
    for (const key of ['results', 'posts', 'data', 'items', 'recent', 'documents']) {
      if (Array.isArray(result[key])) return result[key];
    }
    // Grouped-by-collection: object whose values are all arrays -> flatten
    const values = Object.values(result);
    if (values.length > 0 && values.every((v) => Array.isArray(v))) {
      return values.flat();
    }
  }
  return [];
};

/**
 * Remove feed items whose created_at is dated in the future (beyond the allowed
 * tolerance). Items with a missing or unparseable date are kept as-is so we
 * don't silently drop legitimate content that simply lacks a clean timestamp.
 *
 * Applied to both the cached and freshly-fetched paths so future-dated records
 * can't slip through via a stale cache entry.
 *
 * Exported so feeds that don't come through this hook (the ranked Glance feed,
 * anything else on its own fetch) can apply the same rule instead of
 * reimplementing it.
 */
export const dropFutureDated = (posts) => {
  if (!Array.isArray(posts)) return [];
  const cutoff = Date.now() + FUTURE_DATE_TOLERANCE_MS;
  return posts.filter((post) => {
    const rawDate = post && post.created_at;
    if (!rawDate) return true; // no date -> keep
    const parsed = new Date(rawDate);
    if (isNaN(parsed.getTime())) return true; // unparseable -> keep
    return parsed.getTime() <= cutoff;
  });
};

/**
 * Custom hook to fetch feed data from API with improved handling
 *
 * Most feeds come from api.govglance.org. A feed whose params carry
 * `crawl=recent` comes from crawl.govglance.org instead — a separate public
 * service with its own host, its own filters (country / state / city / level /
 * since) and no API key. That request is built and normalized in
 * crawlFeedAdapter; everything downstream of this hook is unaffected.
 *
 * A falsy apiEndpoint means "nothing to fetch" and is a no-op. Callers whose
 * params depend on state that isn't resolved yet (a saved city, a viewport
 * check) can pass null instead of a placeholder — without this the empty string
 * would fall through to an unfiltered /posts/recent request.
 *
 * `limit` is a live input, not a one-time setting: raising it (paging a list
 * with a Load more button) re-runs the request and returns the longer list.
 * The previous items stay in state while that request is in flight, so the
 * caller can keep rendering them and use `loading` to disable its button.
 * 
 * @param {string} apiEndpoint - API endpoint to fetch data from
 * @param {number} limit - Maximum number of items to fetch, defaults to 1
 * @returns {Object} - Object containing data, loading state, and error
 */
const useFeedData = (apiEndpoint, limit = 1) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!apiEndpoint) {
      setData([]);
      setError(null);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      const cacheKey = `feed_${apiEndpoint}_${limit}`;
      const now = Date.now();
      let hasCachedData = false;

      // Every run starts in flight. Without this the flag stayed false after the
      // first load, so a later run (a raised `limit`, a changed endpoint) gave
      // callers no way to tell a request was pending. `data` is deliberately
      // left alone so the list doesn't blank out mid-request.
      setLoading(true);
      setError(null);

      // Over-fetch so we can drop future-dated items and still fill `limit`.
      const fetchLimit = limit + FUTURE_DATE_FETCH_BUFFER;
      
      // Clean up expired cache entries
      clearExpiredCache();
      
      // First try to get data from localStorage
      try {
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
        
        // Use cached data if it exists and is less than 5 minutes old
        if (cachedData && cachedTimestamp && (now - parseInt(cachedTimestamp, 10) < CACHE_DURATION)) {
          setData(dropFutureDated(extractPostsArray(JSON.parse(cachedData))).slice(0, limit));
          setLoading(false);
          hasCachedData = true;
          // Continue fetching in the background to update the cache
        }
      } catch (cacheError) {
        // Corrupted cache or localStorage unavailable - clear it and continue with fetch
        console.warn('Could not read cached feed data:', cacheError.message);
        try {
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(`${cacheKey}_timestamp`);
        } catch {
          // Ignore cleanup errors
        }
      }

      try {
        // Ensure proper URL construction
        let cleanEndpoint = apiEndpoint;
        
        // Remove any leading & or ? characters
        cleanEndpoint = cleanEndpoint.replace(/^[&?]+/, '');
        
        // Remove any ? characters anywhere in the string (should only be & separators)
        cleanEndpoint = cleanEndpoint.replace(/\?/g, '&');
        
        // Parse params to decide which endpoint shape to use
        const params = new URLSearchParams(cleanEndpoint);
        const hasTableName = cleanEndpoint.includes('table_name=');
        const hasTable = !!params.get('table');
        const hasSchema = !!params.get('schema');

        // Crawl feeds go to a different service entirely — different host,
        // different filters, no Authorization header (it takes no key, and
        // sending one would trip the CORS preflight).
        const isCrawl = isCrawlFeed(cleanEndpoint);

        // The crawl service pages rather than skipping, so the carousels'
        // skip=N is served by over-fetching and slicing below.
        const crawlSkip = isCrawl ? (Number(params.get('skip')) || 0) : 0;
        
        // Determine which API endpoint to use based on parameters.
        // Request fetchLimit (limit + buffer) so future-dated items can be dropped.
        let fullUrl;
        
        if (isCrawl) {
          fullUrl = crawlUrl(cleanEndpoint, { size: crawlSkip + fetchLimit });
        } else if (hasTableName) {
          // Department feeds - use departments endpoint
          fullUrl = `https://api.govglance.org/departments/?schema_name=united_states_of_america&${cleanEndpoint}&limit=${fetchLimit}`;
        } else if (hasSchema && !hasTable) {
          // Schema-level recents (e.g. Canada): a whole-schema recent feed with no single
          // table. -> https://api.govglance.org/posts/recent/schema?limit=10&skip=0&schema=canada
          fullUrl = `https://api.govglance.org/posts/recent/schema?limit=${fetchLimit}&${cleanEndpoint}`;
        } else {
          // Regular feeds - use posts/recent endpoint
          fullUrl = `https://api.govglance.org/posts/recent?limit=${fetchLimit}&${cleanEndpoint}`;
        }
        
        // console.log('Fetching from URL:', fullUrl); // Debug log
        
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: isCrawl
            ? { 'Accept': 'application/json' }
            : {
                'Content-Type': 'application/json',
                // eslint-disable-next-line no-useless-concat
                'Authorization': 'Bearer ' + apiKey
              }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Normalize to a flat array, drop future-dated items, then trim back to
        // the requested limit so callers (e.g. limit=1 cards) still get content.
        let items;
        if (isCrawl) {
          const crawlResults = Array.isArray(result.results) ? result.results : [];
          items = dropFutureDated(crawlResults.map(normalizeCrawlItem))
            .slice(crawlSkip, crawlSkip + limit);
        } else {
          items = dropFutureDated(extractPostsArray(result)).slice(0, limit);
          if (!Array.isArray(result)) {
            console.warn('useFeedData: non-array response normalized for', fullUrl, '-> items:', items.length, result);
          }
        }

        // Update the state with fresh data
        setData(items);
        
        // Try to cache, but don't fail if storage is full or unavailable.
        // Cache the already-filtered/trimmed items so the cache stays clean too.
        try {
          localStorage.setItem(cacheKey, JSON.stringify(items));
          localStorage.setItem(`${cacheKey}_timestamp`, now.toString());
        } catch (storageError) {
          // localStorage is full or unavailable - that's fine, just skip caching
          console.warn('Could not cache feed data:', storageError.message);
        }
        
      } catch (err) {
        console.error('Error fetching feed data:', err);
        // Only set error if we don't have cached data to fall back on
        if (!hasCachedData) {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiEndpoint, limit]);

  return { data, loading, error };
};

export default useFeedData;